import React, { useMemo, useRef, useState, useImperativeHandle } from 'react';
import { Button, Form, Spin } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import {
  RemoteSelect,
  isEmpty,
  normalizeToSelect,
  useCrud,
  CrudEditContextProvider,
  RESOURCE_ACTION_EDIT
} from '@unicus/react-ui-kit';
import generateUUID from 'util/functions/generateUUID';
import RemoteSelectV2 from './RemoteSelectV2';

const CreateEditComponentWrap = ({
  children,
  data,
  resource,
  resourceTitle,
  ...rest
}) => {
  const [form] = Form.useForm();

  return (
    <CrudEditContextProvider form={form}>
      {React.cloneElement(children, {
        ...rest,
        data: data.resourceToEdit,
        saving: data.saving,
        loading: data.loading,
        editing: data.editing,
        loadingEdition: data.loadingEdition,
        readOnly: data.readOnly,
        visible: data.visible || data.editing,
        action: data.action,
        title: `${data.action !== RESOURCE_ACTION_EDIT ? 'Novo ' : 'Editar '} ${
          resourceTitle ? resourceTitle?.toLowerCase() : ''
        }`,
        resource
      })}
    </CrudEditContextProvider>
  );
};

const RemoteSelectCrud = (
  {
    editComponent: EditComponent,
    resource,
    textPropName,
    resourceTitle,
    enableEdit = true,
    onChange,
    value,
    onCreateOrUpdate,
    ...rest
  },
  ref
) => {
  const crud = useCrud({ resource });
  const {
    create,
    get,
    data: { editing, action, componentKey: stateComponentKey }
  } = crud;

  const selectRef = useRef(null);
  const [text, setText] = useState(null);
  const [componentKey, setComponentKey] = useState(generateUUID());
  const [allowShow, setAllowShow] = useState(true);
  const isMultiple = rest.mode === 'multiple';

  const handleChange = (item) => {
    onChange?.(item);
  };

  const handleAddClick = () => {
    selectRef.current.focus();
    selectRef.current.blur();
    setText(null);

    if (!isMultiple) {
      handleChange({});
    }

    setTimeout(() => {
      create(textPropName ? { [textPropName]: text } : {}, { componentKey });
    }, 500);
  };

  const handleEditClick = () => {
    const { id } = value;
    get(id, { componentKey });
  };

  const handleEditClickTag = (id) => {
    get(id, { componentKey });
  };

  const notFoundRender = () => {
    return (
      allowShow && (
        <Button
          className="gx-remote-select-crud-add-button"
          onClick={handleAddClick}
        >
          + Adicionar{text ? ` (${text}...)` : ''}
        </Button>
      )
    );
  };

  const customDropdownRender = ({ menu, fetching }) => {
    return (
      <>
        {menu}
        {fetching ? (
          <div className="gx-remote-select-spin">
            <Spin size="default" />
          </div>
        ) : (
          notFoundRender()
        )}
      </>
    );
  };

  const handleCreateOrUpdateSuccess = ({ data: result }) => {
    selectRef.current?.reset();

    if (isMultiple) {
      const newValue = [...value];
      const index = value.findIndex((c) => c.id === result.result.id);

      if (index > -1) {
        newValue[index] = result.result;
      } else {
        newValue.push(result.result);
      }

      handleChange(newValue);
    } else {
      handleChange(result.result);
    }

    setComponentKey(generateUUID());
    onCreateOrUpdate?.(result.result);
  };

  const handleSearch = (text) => {
    setAllowShow(false);
    setText(text);
  };

  const handleFetched = () => {
    setAllowShow(true);
  };

  const handleDropdownVisibleChange = (visible) => {
    if (!visible) {
      setText(null);
    }
  };

  const tagRender = ({ label, onClose, value }) => {
    return (
      <div className="ant-select-selection-item">
        <span className="gx-mr-2">{label}</span>
        <div>
          <EditOutlined
            className="gx-mr-1"
            onClick={() => handleEditClickTag(value)}
          />
          <CloseOutlined onClick={onClose} />
        </div>
      </div>
    );
  };

  useImperativeHandle(ref, () => selectRef.current);

  return (
    <span
      className={`gx-remote-select-crud ant-input-affix-wrapper${
        !isEmpty(value) ? ' selected' : ''
      }`}
    >
      <RemoteSelectV2
        resource={resource}
        onSearch={handleSearch}
        onChange={handleChange}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        customDropdownRender={customDropdownRender}
        onFetched={handleFetched}
        tagRender={isMultiple ? tagRender : undefined}
        ref={selectRef}
        value={value}
        {...rest}
      />
      {!isMultiple && enableEdit && !isEmpty(value) && (
        <Button className onClick={handleEditClick} icon={<EditOutlined />} />
      )}

      {stateComponentKey === componentKey && (
        <CreateEditComponentWrap
          onCreateOrUpdateSuccess={handleCreateOrUpdateSuccess}
          resource={resource}
          resourceTitle={resourceTitle}
          {...crud}
        >
          <EditComponent />
        </CreateEditComponentWrap>
      )}
    </span>
  );
};

export default React.forwardRef(RemoteSelectCrud);
